import type {
  AutoClaimedWinStreakTowerDto,
  WinStreakTowerDto,
  WinStreakTowerProgressDto,
  WinStreakTowerRewardDto,
} from "../api/dto/win_streak_tower.dto.js";
import { featureDateTime, logRewardCoins, rewardsLabel, trackFeatureEvent } from "../analytics/feature_events.js";

function eventId( tower: WinStreakTowerDto ): string
{
  return `tower@${tower.period_start}`;
}

function common( tower: WinStreakTowerDto ): Record<string, unknown>
{
  return {
    event_id: eventId( tower ),
    config_version: tower.version,
    date_start: featureDateTime( tower.period_start ),
    date_end: featureDateTime( tower.period_end ),
    floors_total: tower.floors.length,
    current_floor: tower.current_floor_id ?? 0,
    wins_progress: tower.wins_progress,
  };
}

/**
 * Win streak tower analytics, derived from the server tower state: started and
 * completed once per tower, a floor reached / claimed once per floor, a streak
 * reset every time a loss wipes progress.
 */
export const WinStreakTowerAnalytics = {
  observe( tower: WinStreakTowerDto | null | undefined ): void
  {
    if ( !tower || tower.floors.length < 2 ) return;
    const id = eventId( tower );
    trackFeatureEvent( "tower_started", common( tower ), id );
    for ( const floor of tower.floors )
    {
      const floor_params = { floor: floor.id, wins_required: floor.wins_required, rewards: rewardsLabel( floor.rewards ) };
      if ( floor.reached ) trackFeatureEvent( "tower_floor_reached", { ...common( tower ), ...floor_params }, `${id}|${floor.id}` );
      if ( floor.claimed ) trackFeatureEvent( "tower_floor_claimed", { ...common( tower ), ...floor_params, auto: false }, `${id}|${floor.id}` );
    }
    if ( tower.completed ) trackFeatureEvent( "tower_completed", common( tower ), id );
  },

  progress( tower: WinStreakTowerDto | null | undefined, progress: WinStreakTowerProgressDto | null | undefined ): void
  {
    if ( !tower || !progress?.streak_reset ) return;
    trackFeatureEvent( "tower_streak_reset", { ...common( tower ), wins_required: progress.wins_required ?? null } );
  },

  claimed( tower: WinStreakTowerDto | null | undefined, floor_id: number, rewards: WinStreakTowerRewardDto[] ): void
  {
    if ( !tower ) return;
    const floor = tower.floors.find( item => item.id === floor_id );
    const sent = trackFeatureEvent( "tower_floor_claimed", {
      ...common( tower ),
      floor: floor_id,
      wins_required: floor?.wins_required ?? null,
      rewards: rewardsLabel( rewards ),
      auto: false,
    }, `${eventId( tower )}|${floor_id}` );
    if ( sent ) logRewardCoins( rewards, "tower_floor" );
  },

  autoClaimed( claimed: AutoClaimedWinStreakTowerDto[] ): void
  {
    for ( const item of claimed )
    {
      let sent = false;
      for ( const floor_id of item.floor_ids )
      {
        sent = trackFeatureEvent( "tower_floor_claimed", {
          event_id: `tower@${item.period_start}`,
          floor: floor_id,
          rewards: rewardsLabel( item.rewards ),
          auto: true,
        }, `tower@${item.period_start}|${floor_id}` ) || sent;
      }
      if ( sent ) logRewardCoins( item.rewards, "tower_auto" );
    }
  },

  opened( tower: WinStreakTowerDto | null | undefined ): void
  {
    if ( !tower ) return;
    trackFeatureEvent( "tower_opened", {
      ...common( tower ),
      claimable_count: tower.floors.filter( floor => floor.claimable && !floor.claimed ).length,
    } );
  },
};
